/**
 * SWR-based API hooks for venture operations
 * Provides data fetching and caching for ventures with appropriate revalidation settings
 */
import useSWR from "swr";
import { api, createHeaders, useServerState } from "@project-mgmt/api-client";
import { handleApiError } from "@project-mgmt/api-client";
/**
 * SWR configuration for public venture data
 * Optimized for public discovery with longer cache times
 * Requirement 15.1-15.5: Performance optimization with caching
 */
const publicVenturesSWRConfig = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000, // 1 minute
    refreshInterval: 300000, // 5 minutes
    shouldRetryOnError: true,
    errorRetryCount: 2,
};
/**
 * SWR configuration for user-specific venture data
 * More aggressive revalidation for authenticated user data
 */
const userVenturesSWRConfig = {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 5000,
    shouldRetryOnError: true,
    errorRetryCount: 3,
};
/**
 * Hook to fetch ventures for a specific workspace
 * Requirement 11.1, 11.2: Workspace venture management
 *
 * @param workspaceId - The workspace ID to fetch ventures for
 * @param filters - Optional filters for status and type
 * @returns SWR response with venture list data
 */
export function useVentures(workspaceId, filters) {
    const params = new URLSearchParams();
    if (filters?.status)
        params.append("status", filters.status);
    if (filters?.type)
        params.append("type", filters.type);
    const queryString = params.toString();
    return useServerState({
        key: [`/api/ventures/workspace/${workspaceId}`, queryString],
        fetcher: async (_, signal) => {
            if (!workspaceId)
                throw new Error("Workspace ID is required");
            const baseUrl = `/api/ventures/workspace/${workspaceId}`;
            const fullUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
            try {
                const response = await fetch(fullUrl, {
                    method: "GET",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to fetch ventures");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        enabled: !!workspaceId,
        config: userVenturesSWRConfig,
        onError: (error) => {
            console.error("Failed to fetch ventures:", error);
        },
    });
}
/**
 * Hook to fetch a single venture by ID
 * Requirement 9.2: Venture detail display
 *
 * @param ventureId - The venture ID to fetch
 * @param workspaceId - The workspace ID for authentication
 * @returns SWR response with venture detail data
 */
export function useVenture(ventureId, workspaceId) {
    const { data, error, isLoading, mutate } = useSWR(ventureId && workspaceId ? [`/api/ventures/${ventureId}`, workspaceId] : null, async ([url, wsId]) => {
        const response = await api.get(url, {
            headers: createHeaders(wsId),
        });
        return response.data;
    }, {
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        dedupingInterval: 10000,
        shouldRetryOnError: true,
        errorRetryCount: 3,
    });
    return {
        venture: data || null,
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
/**
 * Hook to fetch public ventures (no authentication required)
 * Requirement 9.1, 9.2: Public venture discovery
 *
 * @param filters - Optional filters for status and type
 * @returns SWR response with public venture list
 */
export function usePublicVentures(filters) {
    const params = new URLSearchParams();
    if (filters?.status)
        params.append("status", filters.status);
    if (filters?.type)
        params.append("type", filters.type);
    const queryString = params.toString();
    const { data, error, isLoading, mutate } = useSWR(["/api/public/ventures", queryString], async ([url]) => {
        const fullUrl = queryString ? `${url}?${queryString}` : url;
        const response = await api.get(fullUrl);
        return response.data;
    }, publicVenturesSWRConfig);
    return {
        ventures: data?.ventures || [],
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
/**
 * Hook to fetch featured ventures for landing page
 * Requirement 10.1, 10.2: Landing page integration with featured ventures
 *
 * @returns SWR response with featured venture list (up to 6)
 */
export function useFeaturedVentures() {
    const { data, error, isLoading, mutate } = useSWR("/api/public/ventures/featured", async (url) => {
        const response = await api.get(url);
        return response.data;
    }, publicVenturesSWRConfig);
    return {
        ventures: data?.ventures || [],
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
/**
 * Hook to fetch a single public venture by ID (no authentication required)
 * Requirement 9.2: Public venture detail display
 *
 * @param ventureId - The venture ID to fetch
 * @returns SWR response with public venture detail data
 */
export function usePublicVenture(ventureId) {
    const { data, error, isLoading, mutate } = useSWR(ventureId ? `/api/public/ventures/${ventureId}` : null, async (url) => {
        const response = await api.get(url);
        return response.data;
    }, publicVenturesSWRConfig);
    return {
        venture: data || null,
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
/**
 * Hook to fetch ventures created by the current user
 * Requirement 11.1, 11.2: User's created ventures
 *
 * @returns SWR response with user's venture list
 */
export function useMyVentures() {
    const { data, error, isLoading, mutate } = useSWR("/api/users/me/ventures", async (url) => {
        const response = await api.get(url);
        return response.data;
    }, userVenturesSWRConfig);
    return {
        ventures: data || [],
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
/**
 * Mutation API hooks for venture operations
 * These hooks use useServerState with manual: true for mutations
 */
/**
 * Hook to create a new venture
 * Requirement 1.1, 1.2: Venture creation with validation
 *
 * @param workspaceId - The workspace ID where the venture will be created
 * @returns Mutation hook for creating ventures
 */
export function useCreateVenture(workspaceId) {
    return useServerState({
        key: ["ventures:create", workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/ventures`, {
                    method: "POST",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to create venture");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to create venture:", error);
        },
    });
}
/**
 * Hook to update an existing venture
 * Requirement 1.3, 3.1, 3.2: Venture editing with authorization
 *
 * @param ventureId - The venture ID to update
 * @param workspaceId - The workspace ID for authorization
 * @returns Mutation hook for updating ventures
 */
export function useUpdateVenture(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures:update", ventureId, workspaceId],
        fetcher: async (input, signal) => {
            try {
                const response = await fetch(`/api/ventures/${ventureId}`, {
                    method: "PUT",
                    headers: createHeaders(workspaceId),
                    body: JSON.stringify(input),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to update venture");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to update venture:", error);
        },
    });
}
/**
 * Hook to delete a venture
 * Requirement 1.4: Venture deletion with authorization
 *
 * @param workspaceId - The workspace ID for authorization
 * @returns Mutation hook for deleting ventures
 */
export function useDeleteVenture(workspaceId) {
    return useServerState({
        key: ["ventures:delete", workspaceId],
        fetcher: async (ventureId, signal) => {
            try {
                const response = await fetch(`/api/ventures/${ventureId}`, {
                    method: "DELETE",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to delete venture");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to delete venture:", error);
        },
    });
}
/**
 * Hook to publish a draft venture
 * Requirement 2.2, 3.5: Venture status transition to 'live'
 *
 * @param ventureId - The venture ID to publish
 * @param workspaceId - The workspace ID for authorization
 * @returns Mutation hook for publishing ventures
 */
export function usePublishVenture(ventureId, workspaceId) {
    return useServerState({
        key: ["ventures:publish", ventureId, workspaceId],
        fetcher: async (_, signal) => {
            try {
                const response = await fetch(`/api/ventures/${ventureId}/publish`, {
                    method: "POST",
                    headers: createHeaders(workspaceId),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to publish venture");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to publish venture:", error);
        },
    });
}
